// components/layout/LinkLauncher.tsx
// Global Link launcher — a floating action button (FAB) fixed to the bottom-right
// of the viewport, so Link is reachable on EVERY page, including headerless /
// full-bleed ones (#288). It replaces the old header split-button.
//
// Link is "always on": the SAME round button is present whether the chat is open
// or closed. Its ambient glow reflects a backgrounded run (#288):
//   • amber + slow spin — Link is working in the background
//   • blue              — Link is parked, needs you
//   • green             — Link finished
//   • red               — the run failed
// With no background run it's the calm resting state.
//
// A banner slides out of the FAB for the attention states (needs-you / done /
// failed) — but ONLY when the chat is closed; while it's open you see the state
// in the chat itself, so the banner would be redundant.
//
// Actions:
//   • tap while OPEN   — collapse Link (routes through the chat's own close, so
//                        an in-flight turn is backgrounded, not lost).
//   • tap while CLOSED — jump into the background run if there is one, else
//                        resume your most-recent chat (#292).
//   • "+" satellite    — start a fresh conversation ("Ny konversation").
//
// Employer-only for now. The candidate/admin Link is a deliberately reserved,
// hidden slot (see REDESIGN_PLAN.md "Link in all three roles") — enable by
// widening the role check when those flows are ready.
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLinkLauncher, waitingCopy, welcomeCopy, WELCOME_QUESTION, type BackgroundRun } from '@/contexts/LinkLauncherContext';
import { useLinkAgent } from '@/contexts/LinkAgentProvider';
import type { TurnWaitingInfo } from '@/lib/api/linkAgentApi';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import { Pencil } from 'lucide-react';

// #302 — localStorage flag so Link's full "Välkommen till Nexilink" greeting
// auto-shows only on the very first visit. Afterwards the user re-summons the
// shorter recall via the "?" satellite.
const WELCOME_SEEN_KEY = 'nx_link_welcome_seen';

// Global open-Link shortcut label, platform-aware (⌘K on mac, Ctrl+K elsewhere).
// Surfaced in the FAB tooltip + the "?" welcome banner so users discover it.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl+K';

// Ambient glow per background-run state. Ring + soft outer glow in the state
// colour. At rest (no background run) it's a SUBTLE Nexilink-teal glow
// (#1A4D4F = rgb(26,77,79)) that gently breathes — see .link-fab-rest in
// globals.css (box-shadow-only keyframe, so the avatar stays crisp).
const REST_GLOW = 'link-fab-rest';
const GLOW: Record<string, string> = {
    running: 'shadow-[0_0_0_3px_rgba(251,191,36,0.35),0_0_26px_7px_rgba(251,191,36,0.45)]',
    waiting: 'shadow-[0_0_0_3px_rgba(59,130,246,0.40),0_0_26px_7px_rgba(59,130,246,0.55)]',
    done: 'shadow-[0_0_0_3px_rgba(16,185,129,0.40),0_0_26px_7px_rgba(16,185,129,0.55)]',
    failed: 'shadow-[0_0_0_3px_rgba(239,68,68,0.40),0_0_26px_7px_rgba(239,68,68,0.55)]',
};

// Banner shown for the attention states (sliding out of the FAB). Running has no
// banner (the glow/spin says it all); returns null otherwise.
//
// `liveState` + the live pending shapes take precedence over the (poll-driven)
// backgroundRun marker: when the persistent stream is parked awaiting the user we
// build the "needs you" copy from the LIVE pending approval / ask_user. Terminal
// done/failed have no live signal, so they still come from `bg`.
function bannerFor(
    bg: BackgroundRun | null,
    liveState: 'running' | 'waiting' | null,
    pendingApproval: { approvals: Array<{ tool_name: string }> } | null,
    pendingAskUser: { question: { prompt: string } } | null,
): { title: string; detail?: string; primary: string } | null {
    if (liveState === 'waiting') {
        // Map the live pause into a TurnWaitingInfo so waitingCopy renders the
        // exact same Swedish copy as the backgroundRun path.
        const w: TurnWaitingInfo | undefined = pendingAskUser
            ? { kind: 'ask_user', summary: pendingAskUser.question.prompt }
            : pendingApproval
              ? { kind: 'approval', tools: pendingApproval.approvals.map((a) => a.tool_name) }
              : undefined;
        return { ...waitingCopy(w), primary: 'Öppna Link' };
    }
    if (liveState === 'running') return null; // glow/spin says it all
    if (!bg) return null;
    if (bg.state === 'waiting') { const c = waitingCopy(bg.waiting); return { ...c, primary: 'Öppna Link' }; }
    if (bg.state === 'done') return { title: 'Link är klar', detail: 'Ditt uppdrag kördes klart i bakgrunden.', primary: 'Öppna' };
    if (bg.state === 'failed') return { title: 'Link stötte på ett problem', detail: 'Bakgrundskörningen kunde inte slutföras.', primary: 'Öppna' };
    return null;
}

const LinkLauncher: React.FC = () => {
    const { user } = useAuth();
    const {
        resumeLatest, stageLink, reopenLink, requestCloseLink, isOpen, windowMode,
        backgroundRun, openBackgroundRun, dismissBackgroundRun,
        welcome, showWelcome, dismissWelcome,
    } = useLinkLauncher();
    // Live stream state from the persistent provider — now that we DON'T detach
    // on close, the FAB reads the running/waiting cue straight from the live turn.
    const { chat, sessionId, requestComposerFocus, composerDraft, recovery } = useLinkAgent();

    const isEmployer = user?.role === 'employer';

    // Global ⌘/Ctrl+K — TOGGLE Link from ANYWHERE it's available: when closed,
    // open it (resume the live session if the provider holds one, else the most-
    // recent chat) and focus the composer; when open, minimise it via the same
    // background-preserving close path as the X / FAB / ESC (an in-flight turn keeps
    // running). Declared BEFORE the early return so the hook order is stable for
    // non-employers too.
    useEffect(() => {
        if (!isEmployer) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
            e.preventDefault();
            if (isOpen) { requestCloseLink(); return; }
            if (sessionId) reopenLink(); else resumeLatest();
            requestComposerFocus();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isEmployer, isOpen, sessionId, reopenLink, resumeLatest, requestCloseLink, requestComposerFocus]);

    // #302 — first-ever visit: auto-show Link's full greeting once, then mark it
    // seen so later visits stay quiet (the "?" satellite re-summons the short
    // recall). Employer-only, matching where the FAB renders.
    useEffect(() => {
        if (!isEmployer) return;
        try {
            if (!localStorage.getItem(WELCOME_SEEN_KEY)) {
                showWelcome('full');
                localStorage.setItem(WELCOME_SEEN_KEY, '1');
            }
        } catch {
            /* localStorage unavailable (private mode) — skip the greeting */
        }
    }, [isEmployer, showWelcome]);

    // LIVE glow state + the waiting-banner dismiss latch. Declared BEFORE the
    // early return so every hook here runs UNCONDITIONALLY (Rules of Hooks — a
    // non-employer render must call the exact same hooks in the same order). The
    // stream persists in the provider, so running/waiting come straight from the
    // live turn (waiting takes precedence: a paused turn needs the user).
    const bg = backgroundRun;
    const liveWaiting = !!chat.pendingApproval || !!chat.pendingAskUser;
    const liveState: 'running' | 'waiting' | null =
        liveWaiting ? 'waiting' : chat.isStreaming ? 'running' : null;
    // A live-waiting banner is driven by the persistent stream (not the transient
    // backgroundRun marker), so it needs a local "dismissed" latch to hide on
    // "Senare"; it re-arms automatically once the waiting state clears.
    const [waitingBannerDismissed, setWaitingBannerDismissed] = useState(false);
    useEffect(() => { if (liveState !== 'waiting') setWaitingBannerDismissed(false); }, [liveState]);

    // Employer-only.
    if (!isEmployer) return null;

    // Prefer the LIVE turn (authoritative while the provider holds the stream);
    // otherwise fall back to the backgroundRun marker for ANY state. done/failed
    // always come from the marker (no live signal), and after a HARD reload the
    // marker also carries running/waiting (seeded from getTurnStatus in
    // LinkLauncherContext) — the provider's stream was destroyed by the reload, so
    // isStreaming is false until Link is reopened + reattaches.
    // #48 — session-recovery (wiped by a backend restart) takes precedence over
    // every other cue: it's the most urgent thing the FAB can say. Map it onto the
    // existing glow palette — 'recovering' → amber loading spin, 'restored' → green
    // flash, 'failed' → red — so recovery reuses the same visual language.
    const recoveryState: BackgroundRun['state'] | null =
        recovery === 'recovering' ? 'running'
        : recovery === 'restored' ? 'done'
        : recovery === 'failed' ? 'failed'
        : null;
    const glowState: BackgroundRun['state'] | null = recoveryState ?? liveState ?? (bg ? bg.state : null);
    const glow = (glowState && GLOW[glowState]) || REST_GLOW;
    // Spin while actively working OR while recovery is in flight (the loading cue).
    const working = glowState === 'running';

    // The banner only makes sense when the chat is closed. Waiting is built from
    // LIVE pending state; done/failed reuse the backgroundRun copy.
    const banner = isOpen || (liveState === 'waiting' && waitingBannerDismissed)
        ? null
        : bannerFor(bg, liveState, chat.pendingApproval, chat.pendingAskUser);
    // Banner actions: prefer re-showing the LIVE session (no reset) over the
    // resume-from-server background-run path. The terminal (done/failed) marker is
    // cleared centrally in LinkLauncherContext the moment Link opens by ANY path,
    // so no per-path clearing here.
    const openFromBanner = () => { if (sessionId) reopenLink(); else openBackgroundRun(); };
    const dismissBanner = () => { setWaitingBannerDismissed(true); dismissBackgroundRun(); };
    // #302 — the welcome banner shows only when closed and nothing more urgent
    // (a background-run banner) is already out.
    const welcomeBanner = !isOpen && !banner && welcome ? welcomeCopy(welcome) : null;

    const title =
        // #48 — recovery copy wins (matches the recovery glow precedence above).
        recovery === 'recovering' ? 'Återansluter till sessionen…'
        : recovery === 'restored' ? 'Sessionen återställd'
        : recovery === 'failed' ? 'Sessionen kunde inte återställas — starta en ny'
        : isOpen
        ? 'Stäng Link'
        : glowState === 'running' ? 'Link arbetar…'
        : glowState === 'waiting' ? 'Link behöver din input — klicka för att öppna'
        : glowState === 'done' ? 'Link är klar — klicka för att öppna'
        : glowState === 'failed' ? 'Bakgrundskörningen misslyckades — klicka för att öppna'
        : `Chatta med Link · ${SHORTCUT_LABEL}`;

    // Tap: collapse if open (via the chat's own close so the turn backgrounds);
    // else re-show the LIVE session if the provider holds one (visibility only —
    // never resets the running turn), else a terminal background-run, else resume
    // the most-recent chat.
    const handleTap = () => {
        if (isOpen) { requestCloseLink(); return; }
        if (sessionId) { reopenLink(); return; }
        if (bg) { openBackgroundRun(); return; }
        resumeLatest();
    };

    return (
        // Nudged further in from the island edge (bottom-8 right-8) so the FAB
        // doesn't crowd the corner.
        //
        // z-index (#D3): at rest the FAB sits at z-40 — ABOVE page content but
        // BELOW the z-50 overlay layer (dialogs/sheets/drawers/sidebar), so an
        // open modal's backdrop covers it instead of the FAB punching through.
        // When Link is OPEN the FAB is its close button and must ride above Link's
        // own surface — but only as high as that surface needs:
        //   • fullscreen (z-[55] blocking backdrop) → FAB z-[60], above it;
        //   • windowed   (z-40 click-through dock)  → FAB z-[45], above the dock
        //     but STILL below the z-50 modal layer, so a dialog opened while Link
        //     is docked comes to the front (the point of docking Link small).
        <div className={`fixed bottom-8 right-8 ${isOpen ? (windowMode === 'windowed' ? 'z-[45]' : 'z-[60]') : 'z-40'} flex flex-col items-end gap-3 print:hidden`}>
            {/* "Link needs you / är klar / problem" banner — slides out of the FAB.
                Hidden while Link is open. */}
            {banner && (
                <div className="w-[20rem] max-w-[calc(100vw-4rem)] rounded-2xl border-2 border-gray-100 bg-white shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-2 slide-in-from-right-2">
                    <div className="flex items-start gap-3">
                        <LinkAvatar className="w-8 h-8 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900">{banner.title}</p>
                            {banner.detail && (
                                <p className="text-sm text-gray-600 mt-0.5 line-clamp-3">{banner.detail}</p>
                            )}
                            <div className="flex items-center gap-2 mt-3">
                                <button
                                    onClick={openFromBanner}
                                    className="px-3 py-1.5 text-sm font-semibold text-white bg-[#1A4D4F] rounded-lg hover:bg-[#143c3e]"
                                >
                                    {banner.primary}
                                </button>
                                <button
                                    onClick={dismissBanner}
                                    className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
                                >
                                    {glowState === 'waiting' ? 'Senare' : 'Stäng'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* #302 — Link's intro banner: full greeting on first visit, short
                recall on demand. The quoted question is the primary action —
                clicking it opens Link with the question staged in the composer. */}
            {welcomeBanner && (
                <div className="w-[20rem] max-w-[calc(100vw-4rem)] rounded-2xl border-2 border-gray-100 bg-white shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-2 slide-in-from-right-2">
                    <div className="flex items-start gap-3">
                        <LinkAvatar className="w-8 h-8 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-bold text-gray-900">{welcomeBanner.title}</p>
                                <button
                                    onClick={dismissWelcome}
                                    aria-label="Stäng"
                                    className="text-gray-400 hover:text-gray-700 text-xs -mt-0.5"
                                >
                                    Stäng
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">
                                {welcomeBanner.lead}{' '}
                                <button
                                    onClick={() => { dismissWelcome(); stageLink({ prompt: WELCOME_QUESTION }); }}
                                    className="font-semibold text-[#1A4D4F] underline decoration-[#1A4D4F]/30 underline-offset-2 hover:decoration-[#1A4D4F]"
                                >
                                    ”{WELCOME_QUESTION}”
                                </button>
                            </p>
                            <p className="text-[11px] text-gray-400 mt-2">
                                Tips: tryck{' '}
                                <kbd className="px-1 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-600 font-medium">{SHORTCUT_LABEL}</kbd>
                                {' '}var som helst för att öppna Link.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* The FAB — always present. The "?" (re-summon Link's short intro)
                slides out to the LEFT of the button, but as a REAL flex sibling in a
                width-collapsing wrapper — not an absolute satellite. Two reasons:
                (1) at rest the wrapper is w-0/overflow-hidden so the "?" takes no
                space and the FAB stays perfectly clean and put; (2) because it lives
                INSIDE the `group`, hovering the "?" itself keeps the group hovered,
                so you can actually reach it (an absolute satellite sat outside the
                group's hover box and vanished the moment you moved toward it). A
                delayed fade-in means a passing hover doesn't pop it. */}
            <div className="relative group flex items-center">
                {/* Unsent-draft badge — top-right corner, teal pencil. Persistent
                    (not hover-gated) so a saved draft is always visible at a glance;
                    only when closed (open → you see the composer). */}
                {!isOpen && composerDraft.trim().length > 0 && (
                    <span
                        title="Du har ett osänt utkast"
                        aria-label="Osänt utkast"
                        className="absolute -top-0.5 -right-0.5 z-10 w-5 h-5 rounded-full bg-[#1A4D4F] text-white flex items-center justify-center shadow-md ring-2 ring-white"
                    >
                        <Pencil className="w-2.5 h-2.5" />
                    </span>
                )}
                {!isOpen && (
                    // Hover-only reveal (NO group-focus-within): clicking the FAB
                    // leaves it focused, and focus-within would then keep the "?"
                    // stuck open after the chat is closed. The "?" is width-collapsed
                    // so it isn't keyboard-reachable anyway — hover is the right (and
                    // only) trigger for this optional help nudge.
                    <div className="overflow-hidden w-0 group-hover:w-9 group-hover:delay-500 transition-[width] duration-200 ease-out flex items-center justify-center">
                        <button
                            onClick={() => (welcome ? dismissWelcome() : showWelcome('short'))}
                            aria-label="Vad är Link?"
                            title="Vad är Link?"
                            className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-[#1A4D4F] border border-gray-200 shadow-md flex items-center justify-center text-xs font-semibold leading-none opacity-0 group-hover:opacity-100 group-hover:delay-500 transition-opacity duration-200 hover:bg-gray-50"
                        >
                            ?
                        </button>
                    </div>
                )}
                <button
                    onClick={handleTap}
                    aria-label={title}
                    title={title}
                    className={`relative w-16 h-16 rounded-full bg-white flex items-center justify-center transition-shadow duration-300 hover:scale-[1.03] active:scale-95 ${glow}`}
                >
                    <LinkAvatar className={`w-9 h-9 ${working ? 'animate-[spin_4s_linear_infinite]' : ''}`} />
                </button>
            </div>
        </div>
    );
};

export default LinkLauncher;
